import { forwardRef, useImperativeHandle } from "react";

import { Stack } from "@mui/material";

import { useDisclosure } from "hooks";
import {
  getPageCount,
  SHIPMENTS_PAGE_SIZE,
  sortSupplierShipmentsByDate,
} from "utils";

import { SupplierShipmentsFilter } from "../SupplierShipmentsFilter";
import { SupplierShipmentsList } from "../SupplierShipmentsList";

export interface SupplierShipmentListContainerRef {
  closeFilter: VoidFunction;
}

interface Props {
  page: number;
  setPage: (pageNumber: number) => void;
  supplierShipments: SupplierShipment[];
  supplierShipmentsError: string | null;
  supplierShipmentsLoading: boolean;
  supplierShipmentCount: number;
  onFilterExpanded?: VoidFunction;
  onFilterCollapsed?: VoidFunction;
}

export const SupplierShipmentListContainer = forwardRef<
  SupplierShipmentListContainerRef,
  Props
>((props, ref) => {
  const {
    page,
    setPage,
    supplierShipments,
    supplierShipmentsError,
    supplierShipmentsLoading,
    supplierShipmentCount,
    onFilterExpanded,
    onFilterCollapsed,
  } = props;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleFilterOpen = () => {
    onOpen();
    onFilterExpanded?.();
  };

  const handleFilterClose = () => {
    onClose();
    onFilterCollapsed?.();
  };

  useImperativeHandle(ref, () => ({ closeFilter: handleFilterClose }));

  const pages = getPageCount(supplierShipmentCount, SHIPMENTS_PAGE_SIZE);

  const handlePageChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
  };

  return (
    <Stack spacing={2}>
      <SupplierShipmentsFilter
        isOpen={isOpen}
        onOpen={handleFilterOpen}
        onClose={handleFilterClose}
      />
      <SupplierShipmentsList
        supplierShipments={sortSupplierShipmentsByDate(supplierShipments)}
        error={supplierShipmentsError}
        isLoading={supplierShipmentsLoading}
        page={page}
        pages={pages}
        handlePageSelected={handlePageChange}
      />
    </Stack>
  );
});
